import axios from 'axios';
import type { CancelTokenSource } from 'axios';

import type { PlaidLinkType } from 'models/plaid';

import type { ServerError } from '../../models';
import type { BankAccountVo } from '../../models/banks';
import type {
    CreateAeropayLinkTokenResponse,
    CreateDeposit,
    CreateDepositResponse,
    CreatePlaidLayerTokenResponse,
    CreatePlaidLinkTokenResponse,
    CreateWithdrawal,
    CreateWithdrawalResponse,
    GetBankAccountsResponse,
    GetCreditHistoryParams,
    GetCreditHistoryResponse,
    GetDepositsParams,
    GetDepositsResponse,
    GetPlaidAccountsResponse,
    GetPlaidImmediateFundingResponse,
    GetWithdrawalsParams,
    GetWithdrawalsResponse,
    InitiatePlaidDepositResponse,
    InitiatePlaidTransferRequest,
    InitiatePlaidWithdrawalResponse,
    LinkAeropayBankAccountRequest,
    LinkAeropayBankAccountResponse,
    LinkPlaidBankAccountRequest,
    LinkPlaidBankAccountResponse,
    RemoveBankAccountRequest,
} from '../../models/reqres';
import type { TransferVo } from '../../models/transfer';
import { exchangeApi, handleErrorCallback, noop } from '../core';

export function getBankAccounts(
    userId: string,
    onSuc: (accounts: Array<BankAccountVo>) => void,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .get<GetBankAccountsResponse>(`/users/${userId}/banks/all_accounts`, {
            cancelToken: cancelRef.token,
            params: { allowRequestEvenIfPageIsHidden: true },
        })
        .then((response) => {
            onSuc(response.data.accounts);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function createAeropayLinkToken(
    userId: string,
    onSuc: (token: string) => void,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .get<CreateAeropayLinkTokenResponse>(`/users/${userId}/aeropay/link_token`, {
            cancelToken: cancelRef.token,
        })
        .then((response) => {
            onSuc(response.data.token);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function linkAeropayAccount(
    userId: string,
    payload: LinkAeropayBankAccountRequest,
    onSuc: (account: BankAccountVo) => void,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .post<LinkAeropayBankAccountResponse>(`/users/${userId}/banks/linked_accounts/aeropay`, payload, {
            cancelToken: cancelRef.token,
        })
        .then((resp) => {
            onSuc(resp.data.account || []);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function createPlaidLinkToken(
    userId: string,
    linkType: PlaidLinkType,
    onSuc: (token: string) => void,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    const endpoint = linkType === 'layer' ? `/users/${userId}/plaid/layer_token` : `/users/${userId}/plaid/link_token`;

    exchangeApi
        .get<CreatePlaidLayerTokenResponse | CreatePlaidLinkTokenResponse>(endpoint, {
            cancelToken: cancelRef.token,
        })
        .then((response) => {
            const token =
                linkType === 'layer'
                    ? (response.data as CreatePlaidLayerTokenResponse).layer_token
                    : (response.data as CreatePlaidLinkTokenResponse).link_token;
            onSuc(token);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function linkPlaidAccount(
    userId: string,
    payload: LinkPlaidBankAccountRequest,
    onSuc: (account: BankAccountVo) => void,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .post<LinkPlaidBankAccountResponse>(`/users/${userId}/plaid/accounts`, payload, {
            cancelToken: cancelRef.token,
        })
        .then((resp) => {
            onSuc(resp.data.account || []);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function getPlaidAccounts(
    userId: string,
    onSuc: (accounts: BankAccountVo[]) => void,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .get<GetPlaidAccountsResponse>(`/users/${userId}/plaid/accounts`, {
            cancelToken: cancelRef.token,
        })
        .then((response) => {
            onSuc(response.data.bank_accounts);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function removePlaidAccount(
    userId: string,
    bankId: string,
    onSuc: (response: InitiatePlaidWithdrawalResponse) => void,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .delete<InitiatePlaidWithdrawalResponse>(`/users/${userId}/plaid/accounts/${bankId}`, {
            cancelToken: cancelRef.token,
        })
        .then((response) => {
            onSuc(response.data);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function getPlaidImmediateFundingDetails(
    userId: string,
    bankId: string,
    amountCents: number,
    onSuc: (response: GetPlaidImmediateFundingResponse) => void,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
    riskifiedCartToken?: string,
    sardineSessionKey?: string,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    let url = `/users/${userId}/plaid/accounts/${bankId}/immediate_funding?amount_cents=${amountCents}`;
    if (riskifiedCartToken) {
        url += `&riskified_cart_token=${encodeURIComponent(riskifiedCartToken)}`;
    }
    if (sardineSessionKey) {
        url += `&sardine_session_key=${encodeURIComponent(sardineSessionKey)}`;
    }

    exchangeApi
        .get<GetPlaidImmediateFundingResponse>(url, {
            cancelToken: cancelRef.token,
        })
        .then((response) => {
            onSuc(response.data);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function initiatePlaidDeposit(
    userId: string,
    payload: InitiatePlaidTransferRequest,
    onSuc: (response: InitiatePlaidDepositResponse) => void,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .post<InitiatePlaidDepositResponse>(`/users/${userId}/plaid/deposit`, payload, {
            cancelToken: cancelRef.token,
        })
        .then((response) => {
            onSuc(response.data);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function initiatePlaidWithdrawal(
    userId: string,
    payload: InitiatePlaidTransferRequest,
    onSuc: (response: InitiatePlaidWithdrawalResponse) => void,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .post<InitiatePlaidWithdrawalResponse>(`/users/${userId}/plaid/withdrawal`, payload, {
            cancelToken: cancelRef.token,
        })
        .then((response) => {
            onSuc(response.data);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function removeBankAccount(
    payload: RemoveBankAccountRequest,
    onSuc: () => void = noop,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .delete(`users/${payload.user_id}/banks/linked_accounts/${payload.bank_id}`, {
            cancelToken: cancelRef.token,
        })
        .then(() => {
            onSuc();
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function createDeposit(
    userId: string,
    payload: CreateDeposit,
    onSuc: ({ deposit_id }: CreateDepositResponse) => void,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .post<CreateDepositResponse>(`/users/${userId}/deposits`, payload, { cancelToken: cancelRef.token })
        .then((response) => {
            onSuc({
                deposit: response.data.deposit,
                deposit_id: response.data.deposit_id,
                estimated_ach_time_days: response.data.estimated_ach_time_days,
            });
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function createWithdrawal(
    userId: string,
    payload: CreateWithdrawal,
    onSuc: ({ withdrawal_id }: CreateWithdrawalResponse) => void,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .post<CreateWithdrawalResponse>(`/users/${userId}/withdrawals`, payload, { cancelToken: cancelRef.token })
        .then((response) => {
            onSuc({
                withdrawal: response.data.withdrawal,
                withdrawal_id: response.data.withdrawal_id,
            });
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function getDeposits(
    userId: string,
    onSuc: (transfers: Array<TransferVo>) => void,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
    params?: GetDepositsParams,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .get<GetDepositsResponse>(`/users/${userId}/deposits`, { cancelToken: cancelRef.token, params })
        .then((response) => {
            onSuc(response.data.deposits || []);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function getWithdrawals(
    userId: string,
    onSuc: (transfers: Array<TransferVo>) => void,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
    params?: GetWithdrawalsParams,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .get<GetWithdrawalsResponse>(`/users/${userId}/withdrawals`, { cancelToken: cancelRef.token, params })
        .then((response) => {
            onSuc(response.data.withdrawals || []);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function getCreditHistory(
    userId: string,
    params: GetCreditHistoryParams,
    onSuc: (credits: GetCreditHistoryResponse) => void,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .get<GetCreditHistoryResponse>(`/users/${userId}/credit_history`, {
            cancelToken: cancelRef.token,
            params: params,
        })
        .then((response) => {
            onSuc(response.data);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}
